import { useEffect, useState } from "react";
import { LoadingCard } from "@xpense/ui";
import { api, Forecast } from "../api/client";
import { EmptyState, MetricCard, Page, RetryButton, Section } from "../components/Page";

export default function ForecastPage() {
  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadForecast();
  }, []);

  const loadForecast = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.get<Forecast>("/api/v1/forecast?period=monthly");
      setForecast(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load forecast");
    } finally {
      setLoading(false);
    }
  };

  const formatINR = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);

  if (loading) {
    return (
      <Page
        eyebrow="Projection"
        title="Forecast"
        description="AI narrative layered over numeric projections and confidence bands."
      >
        <div className="xpn-grid xpn-grid--3">
          <LoadingCard />
          <LoadingCard />
          <LoadingCard />
        </div>
        <LoadingCard />
      </Page>
    );
  }

  if (error) {
    return (
      <Page eyebrow="Projection" title="Forecast">
        <Section>
          <EmptyState
            icon="warning"
            title="Unable to load forecast"
            description={error}
            action={<RetryButton onClick={loadForecast} />}
            tone="danger"
          />
        </Section>
      </Page>
    );
  }

  if (!forecast) {
    return (
      <Page
        eyebrow="Projection"
        title="Forecast"
        description="Forecasts appear after enough transaction history has been classified."
      >
        <Section>
          <EmptyState
            icon="forecast"
            title="No forecast available"
            description="Add more transactions to generate spending projections."
          />
        </Section>
      </Page>
    );
  }

  const categoryMax = Math.max(
    1,
    ...forecast.category_projections.map((cat) => cat.projected_amount)
  );

  return (
    <Page
      eyebrow="Projection"
      title="Next month forecast"
      description="Projected spend, confidence range, and the factors the agent expects to move your budget."
    >
      <div className="xpn-grid xpn-grid--3">
        <MetricCard
          label="Projected total"
          value={formatINR(forecast.total_projected)}
          caption="Expected monthly outflow"
          tone="info"
        />
        <MetricCard
          label="Low estimate"
          value={formatINR(forecast.confidence_low)}
          caption="Lower confidence band"
          tone="success"
        />
        <MetricCard
          label="High estimate"
          value={formatINR(forecast.confidence_high)}
          caption="Upper confidence band"
          tone="warning"
        />
      </div>

      <Section
        title="Confidence band"
        description="The model's projected spend sits within this range."
      >
        <div className="xpn-section-body">
          <div className="xpn-flex-between xpn-mb-sm">
            <span data-tnum className="xpn-text-caption-tabular xpn-text-muted">
              {formatINR(forecast.confidence_low)}
            </span>
            <span data-tnum className="xpn-text-caption-tabular xpn-text-muted">
              {formatINR(forecast.confidence_high)}
            </span>
          </div>
          <div className="xpn-progress" style={{ height: 14 }}>
            <div
              className="xpn-progress-bar"
              style={{
                width: "64%",
                marginLeft: "18%",
                background: "var(--xpn-color-info)",
              }}
            />
          </div>
          <div className="xpn-text-caption xpn-text-muted xpn-mt-sm">
            Center point: {formatINR(forecast.total_projected)}
          </div>
        </div>
      </Section>

      <div className="xpn-grid xpn-grid--2">
        <Section title="Category projections" description="Expected spend by category.">
          <div className="xpn-section-body">
            {forecast.category_projections.length > 0 ? (
              forecast.category_projections.map((cat) => (
                <div className="xpn-category-row" key={cat.category}>
                  <div>
                    <div className="xpn-flex-between xpn-mb-xs">
                      <span className="xpn-text-body-md xpn-text" style={{ fontWeight: 650 }}>
                        {cat.category}
                      </span>
                      <span className="xpn-text-caption xpn-text-muted">{cat.trend}</span>
                    </div>
                    <div className="xpn-progress">
                      <div
                        className="xpn-progress-bar"
                        style={{
                          width: `${Math.max(4, (cat.projected_amount / categoryMax) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div data-tnum className="xpn-text-body-tabular xpn-text">
                    {formatINR(cat.projected_amount)}
                  </div>
                </div>
              ))
            ) : (
              <EmptyState title="No category projections" description="Category projections need more history." />
            )}
          </div>
        </Section>

        <Section title="Top drivers" description="Signals affecting the next forecast.">
          <div className="xpn-section-body">
            {forecast.top_drivers.length > 0 ? (
              <div className="xpn-row-list">
                {forecast.top_drivers.map((driver) => (
                  <div key={driver} style={{ padding: "var(--xpn-space-md) 0" }}>
                    <div className="xpn-text-body-md xpn-text">{driver}</div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title="No drivers yet" description="Drivers appear once the projection agent has context." />
            )}
          </div>
        </Section>
      </div>

      <Section title="Assumptions" description="What the projection agent used as context.">
        <div className="xpn-section-body">
          {forecast.assumptions.length > 0 ? (
            <div className="xpn-grid xpn-grid--2">
              {forecast.assumptions.map((assumption) => (
                <div
                  key={assumption}
                  style={{
                    padding: "var(--xpn-space-lg) 0",
                    borderTop: "1px solid var(--xpn-border-subtle)",
                  }}
                >
                  <p className="xpn-text-body-md xpn-text-muted">{assumption}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No assumptions returned" description="The forecast did not include assumptions." />
          )}
        </div>
      </Section>
    </Page>
  );
}
